import React, { useState } from "react"
import styled, { css } from "styled-components"
import { GatsbyImage } from "gatsby-plugin-image"

const size = {
  tablet: 950,
  mobile: 600,
}
const below = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  background-color: var(--alt-black);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`
const ModalCard = styled.div`
  background-color: white;
  border-radius: 5px;
  margin: 0 48px;
  padding: 40px 48px 0;
  width: 100%;
  max-width: 768px;
  max-height: 90vh;
  overflow-y: scroll;
  position: relative;
  box-shadow: 0px 4.8412px 29.0472px rgba(19, 101, 186, 0.15);
  ${below.mobile`
    padding: 32px 16px;
    margin: 0 24px;
    `};
  hr {
    margin: 0 0 24px;
    padding: 0;
    border-bottom: 1px solid #e2e8f0;
  }
`
const SelectionSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${below.tablet`
  display: block;
  max-width: 480px;
  margin: 0 auto;
  `}
`
const TeamContainer = styled.div`
  width: 50%;
  min-width: 326px;
  margin: 0 0 24px;
  ${below.tablet`
  width: 100%;
  min-width: initial;
  `}
  &:nth-of-type(odd) {
    padding-right: 16px;
    ${below.tablet`padding: 0;`}
  }
`
const TeamCard = styled.button`
  outline: none;
  cursor: pointer;
  text-align: left;
  padding: 24px 16px;
  background-color: white;
  border: 1px solid #cbd2dd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  transition: 0.3s ease-in-out;

  :hover {
    border: 1px solid #1e8afa;
  }

  &.selected {
    background-color: rgba(30, 138, 250, 0.05);
    border: 1px solid #1e8afa;
  }
`
const Title = styled.h2`
  font-family: "Playfair Display", serif;
  font-size: 40px;
  line-height: 56px;
  font-weight: 400;
  margin-bottom: 8px;
`
const Description = styled.p`
  font-family: "Averta PE", sans-serif;
  font-size: 18px;
  line-height: 24px;
  color: #4a5568;
  margin-bottom: 24px;
`
const LeftSideContainer = styled.div`
  display: flex;
  padding-right: 24px;
`
const Headshot = styled(GatsbyImage)`
  border-radius: 50%;
  margin-right: 20px;
  display: inline-block;
  width: 48px;
  height: 48px;
  ${below.mobile`
  margin-right: 8px;
  `}
`
const Headshotless = styled.div`
  margin-right: 20px;
  border-radius: 50%;
  background-color: #264765;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 900;
  font-size: 12px;
  line-height: 48px;
  width: 48px;
  height: 48px;
`
const Name = styled.div`
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
`
const Licensing = styled.div`
  font-family: "Playfair Display", serif;
  font-size: 12px;
  line-height: 16px;
  color: #4a5568;
`
const Radio = styled.div`
  position: relative;
  border-radius: 50%;
  height: 16px;
  width: 16px;
  border: 1px solid #cbd2dd;
  transition: 0.3s ease-in-out;
  :after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    background-color: #fff;
    height: 6px;
    width: 6px;
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }
  &.selected {
    background-color: #1e8afa;
    border: 1px solid #1e8afa;
  }
`
const ActionContainer = styled.div`
  display: flex;
  flex-direction: flex-row-reverse;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  ${below.mobile`
  display: block;
  
  `}
`
const Button = styled.a`
  color: white;
  display: block;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  padding: 16px 32px;
  margin: 0;
  border-radius: 2.5px;
  background-color: #1e8afa;
  text-decoration: none;
  text-align: center;
  opacity: 0.35;
  pointer-events: none;
  &.enabled {
    opacity: 1;
    pointer-events: initial;
  }
  :visited {
    text-decoration: none;
    color: white;
  }
  :hover {
    text-decoration: none;
  }
  ${below.mobile`
    font-size: 18px;
    line-height: 24px;
    display: block;
    margin: 0 auto 24px;
    `};
`

const SeimKirkCalendarModal = props => {
  const [selected, setSelected] = useState(false)
  return (
    <ModalWrapper>
      <ModalCard>
        <Title>Ready to meet?</Title>
        <Description>
          Select the Mortgage Banker that you would like to schedule an
          appointment with.
        </Description>

        <SelectionSection>
          {props.team.map(teammate => (
            <TeamContainer key={teammate.fullName}>
              <TeamCard
                onClick={() =>
                  selected && selected.fullName === teammate.fullName
                    ? setSelected(false)
                    : setSelected(teammate)
                }
                className={
                  selected && selected.fullName === teammate.fullName
                    ? "selected"
                    : undefined
                }
              >
                <LeftSideContainer>
                  {teammate.headshot ? (
                    <Headshot
                      image={teammate.headshot.gatsbyImageData}
                      alt={teammate.fullName}
                      placeholderStyle={{ filter: "blur(6px)" }}
                    />
                  ) : (
                    <Headshotless>{`${teammate.firstName.charAt()}${teammate.lastName.charAt()}`}</Headshotless>
                  )}

                  <div>
                    <Name>{teammate.fullName}</Name>
                    <Licensing>{`NMLS #${teammate.nmls}`}</Licensing>

                    <Licensing>
                      {`Licensed in `}
                      {teammate.licenses.map(licensedState => (
                        <span key={licensedState}>
                          {`${licensedState}${
                            teammate.licenses.length === 2 &&
                            teammate.licenses.indexOf(licensedState) <
                              teammate.licenses.length - 1
                              ? " & "
                              : ""
                          }${
                            teammate.licenses.length > 2 &&
                            teammate.licenses.indexOf(licensedState) <
                              teammate.licenses.length - 1
                              ? ", "
                              : ""
                          }`}
                        </span>
                      ))}
                    </Licensing>
                  </div>
                </LeftSideContainer>

                <Radio
                  className={
                    selected && selected.fullName === teammate.fullName
                      ? "selected"
                      : undefined
                  }
                />
              </TeamCard>
            </TeamContainer>
          ))}
        </SelectionSection>

        <hr />
        <ActionContainer>
          <Button
            className={selected ? "enabled" : "disabled"}
            href={selected.calendarLink}
          >
            {!selected
              ? `Make selection`
              : `Schedule with ${selected.firstName}`}
          </Button>
        </ActionContainer>
      </ModalCard>
    </ModalWrapper>
  )
}

export default SeimKirkCalendarModal
